import api from 'service/api-service';
import moment from 'moment';
import { saveAuth, saveUserId } from 'service/local-data-provider';

class UserRepository {
  login = (username, password, subAccountUsername) => {
    const subAccountUsernameInput = subAccountUsername === '' ? undefined : subAccountUsername;
    return new Promise((resolve, reject) => {
      api
        .auth(username, password, subAccountUsernameInput)
        .then(value => {
          const { data } = value.data;
          saveAuth(data);
          resolve(data);
        })
        .catch(error => reject(error));
    });
  };

  logout = () => {
    return new Promise((resolve, reject) => {
      api
        .logout()
        .then(() => resolve())
        .catch(error => reject(error));
    });
  };

  changePassword = (oldPassword, newPassword) => {
    return new Promise((resolve, reject) => {
      api
        .changePassword(oldPassword, newPassword)
        .then(value => {
          const { data } = value.data;
          saveAuth(data);
          resolve(data);
        })
        .catch(error => reject(error));
    });
  };

  authTotp = totp => {
    return new Promise((resolve, reject) => {
      api
        .authTotp(totp)
        .then(value => {
          const { data } = value.data;
          saveAuth(data);
          resolve(data);
        })
        .catch(error => reject(error));
    });
  };

  userInfo = () => {
    return new Promise((resolve, reject) => {
      api
        .userInfo()
        .then(value => {
          saveUserId(value.data.data.id);
          resolve(value.data.data);
        })
        .catch(error => reject(error));
    });
  };

  updateUserInfo = (email = undefined, displayName = undefined, receipt = undefined) => {
    return new Promise((resolve, reject) => {
      api
        .updateUserInfo(email, displayName, receipt)
        .then(value => resolve(value.data.data))
        .catch(error => reject(error));
    });
  };

  fetchLicensePlates = (searchPlateNumber, page = undefined, perPage = 100) => {
    const searchLicensePlateNumberInput = searchPlateNumber === '' ? undefined : searchPlateNumber;
    return new Promise((resolve, reject) => {
      api
        .fetchLicensePlates({ plateNumberCont: searchLicensePlateNumberInput, page, perPage })
        .then(value => resolve(value.data))
        .catch(error => reject(error));
    });
  };

  fetchLicensePlatesV2 = ({ plateNumberCont, page, perPage }) => {
    return new Promise((resolve, reject) => {
      api
        .fetchLicensePlates({ plateNumberCont, page, perPage })
        .then(value => resolve(value))
        .catch(error => reject(error));
    });
  };

  fetchEnterpriseDrivers = async ({ page, perPage, mobileCont }) => {
    return new Promise((resolve, reject) => {
      api
        .fetchEnterpriseDrivers({ page, perPage, mobileCont })
        .then(value => resolve(value))
        .catch(error => reject(error));
    });
  };

  fetchEnterpriseDriversRefuelingAmountInfo = async () => {
    return new Promise((resolve, reject) => {
      api
        .fetchEnterpriseDriversRefuelingAmountInfo()
        .then(value => resolve(value))
        .catch(error => reject(error));
    });
  };

  updateEnterpriseLicensePlateTaxIdNumber = async ({ licensePlateId, taxIdNumber }) => {
    return new Promise((resolve, reject) => {
      api
        .updateEnterpriseLicensePlateTaxIdNumber({ licensePlateId, taxIdNumber })
        .then(value => resolve(value))
        .catch(error => reject(error));
    });
  };

  createEnterpriseDriver = ({ mobile }) => {
    return new Promise((resolve, reject) => {
      api
        .createEnterpriseDriver({ mobile })
        .then(value => resolve(value))
        .catch(error => reject(error));
    });
  };

  deleteEnterpriseDriver = ({ id }) => {
    return new Promise((resolve, reject) => {
      api
        .deleteEnterpriseDriver({ id })
        .then(value => resolve(value))
        .catch(error => reject(error));
    });
  };

  fetchEnterpriseTaxSettings = () => {
    return new Promise((resolve, reject) => {
      api
        .fetchEnterpriseTaxSettings()
        .then(value => resolve(value))
        .catch(error => reject(error));
    });
  };

  createLicensePlate = ({ plateNumber, energySource, vehicleType, oilType }) => {
    return new Promise((resolve, reject) => {
      api
        .createLicensePlate({ plateNumber, energySource, vehicleType, oilType })
        .then(value => resolve(value))
        .catch(error => reject(error));
    });
  };

  deleteLicensePlate = id => {
    return new Promise((resolve, reject) => {
      api
        .deleteLicensePlate(id)
        .then(value => resolve(value))
        .catch(error => reject(error));
    });
  };

  updateLicensePlate = ({ id, plateNumber, energySource, vehicleType, oilType }) => {
    return new Promise((resolve, reject) => {
      api
        .updateLicensePlate({ id, plateNumber, energySource, vehicleType, oilType })
        .then(value => resolve(value))
        .catch(error => reject(error));
    });
  };

  initMochipayValidation = () => {
    return new Promise((resolve, reject) => {
      api
        .initMochipayValidation()
        .then(value => resolve(value.data.data))
        .catch(error => reject(error));
    });
  };

  bindMochipayWallet = (token, transactionId) => {
    return new Promise((resolve, reject) => {
      api
        .bindMochipayWallet(token, transactionId)
        .then(value => resolve(value.data.data))
        .catch(error => reject(error));
    });
  };

  deleteCreditCard = () => {
    return new Promise((resolve, reject) => {
      api
        .deleteCreditCard()
        .then(value => resolve(value))
        .catch(error => reject(error));
    });
  };

  fetchDebitAccountTransactions = (id, startAt, endAt, page, totalPage) => {
    return new Promise((resolve, reject) => {
      api
        .fetchDebitAccountTransactions(id, startAt, endAt, page, totalPage)
        .then(value => resolve(value.data))
        .catch(error => reject(error));
    });
  };

  sendDebitAccountTransactionsReport = (id, startAt, endAt) => {
    return new Promise((resolve, reject) => {
      api
        .sendDebitCardReport(
          id,
          startAt ? moment(startAt).toISOString(true) : undefined,
          endAt ? moment(endAt).toISOString(true) : undefined
        )
        .then(() => resolve())
        .catch(error => reject(error));
    });
  };

  fetchTwoFactorAuth = () => {
    return new Promise((resolve, reject) => {
      api
        .fetchTwoFactorAuth()
        .then(value => resolve(value.data.data))
        .catch(error => reject(error));
    });
  };

  enableTwoFactorAuth = totp => {
    return new Promise((resolve, reject) => {
      api
        .enableTwoFactorAuth(totp)
        .then(value => resolve(value.data.data))
        .catch(error => reject(error));
    });
  };

  disableTwoFactorAuth = () => {
    return new Promise((resolve, reject) => {
      api
        .disableTwoFactorAuth()
        .then(value => resolve(value.data.data))
        .catch(error => reject(error));
    });
  };

  fetchOrders = (page, pageSize, transactionTypes, plateNumber, startAt, endAt) => {
    return new Promise((resolve, reject) => {
      api
        .fetchOrders(
          page,
          pageSize,
          transactionTypes.length === 0 ? undefined : transactionTypes.join(','),
          plateNumber,
          startAt ? moment(startAt).toISOString(true) : undefined,
          endAt ? moment(endAt).toISOString(true) : undefined
        )
        .then(value => resolve(value.data))
        .catch(error => reject(error));
    });
  };

  fetchOrderDetail = url => {
    return new Promise((resolve, reject) => {
      api
        .fetchOrderDetail(url)
        .then(value => resolve(value.data))
        .catch(error => reject(error));
    });
  };

  fetchCoupons = ({ state, page, perPage }) => {
    return new Promise((resolve, reject) => {
      api
        .fetchCoupons({ state, page, perPage })
        .then(value => resolve(value))
        .catch(error => reject(error));
    });
  };

  sendUserReport = (transactionTypes, startAt, endAt, plateNumber) => {
    return new Promise((resolve, reject) => {
      api
        .sendUserReport(transactionTypes, startAt, endAt, plateNumber)
        .then(() => resolve())
        .catch(error => reject(error));
    });
  };

  fetchMochiCoinTransactions = (state, page, pageSize) => {
    return new Promise((resolve, reject) => {
      api
        .fetchMochiCoinTransactions(state, page, pageSize)
        .then(value => resolve(value.data))
        .catch(error => reject(error));
    });
  };
}

export default UserRepository;
